/**
 * UsagesPanel - Displays list usages in a floating panel
 */
import React, { useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faArrowUpRightFromSquare, faSearch } from '@fortawesome/free-solid-svg-icons'
import { useDraggablePanel } from 'hooks/core/useDraggablePanel'

interface Usage {
  lineNumber: number
  content: string
  type?: 'FROM' | 'PER' | 'RANDOMIZE' | 'DEFINITION'
}

interface UsagesPanelProps {
  /** Name of the list being shown */
  listName: string
  /** Array of usages */
  usages: Usage[]
  /** Callback when a usage line is clicked */
  onLineClick: (lineNumber: number) => void
  /** Callback to close the panel */
  onClose: () => void
  /** Editor container ref for positioning */
  editorContainerRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Determine usage type from content
 */
const getUsageType = (content: string, listName: string, _index: number): 'FROM' | 'PER' | 'RANDOMIZE' | 'DEFINITION' | undefined => {
  const trimmed = content.trim()

  // Check if it's a definition line
  if (trimmed.match(/^(RANGE\s+)?LIST\s+/i)) return 'DEFINITION'

  // Check for usage types
  if (trimmed.match(/\bFROM\b/i)) return 'FROM'
  if (trimmed.match(/\bPER\b/i)) return 'PER'
  if (trimmed.match(/\bRANDOMIZE\b/i)) return 'RANDOMIZE'

  // Check if it's a list name on its own line (could be FROM or PER reference)
  if (trimmed === listName) {
    // This is ambiguous, we need context from previous lines
    return undefined
  }
  return undefined
}

/**
 * Get badge color based on usage type
 */
const getBadgeColor = (type?: 'FROM' | 'PER' | 'RANDOMIZE' | 'DEFINITION'): string => {
  switch (type) {
    case 'DEFINITION':
      return 'bg-green-600'
    case 'FROM':
      return 'bg-blue-500'
    case 'PER':
      return 'bg-purple-500'
    case 'RANDOMIZE':
      return 'bg-orange-500'
    default:
      return 'bg-gray-500'
  }
}

export const UsagesPanel: React.FC<UsagesPanelProps> = ({
  listName,
  usages,
  onLineClick,
  onClose,
  editorContainerRef,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel()

  // Set initial position near the right edge of the editor (only once)
  useEffect(() => {
    if (!panelRef.current || !editorContainerRef.current) return
    if (position.x !== 0 || position.y !== 0) return // Already positioned

    const editorRect = editorContainerRef.current.getBoundingClientRect()
    const panel = panelRef.current
    const panelRect = panel.getBoundingClientRect()

    // Position on the right side of the editor, with some margin
    const initialX = window.innerWidth - panelRect.width - 20
    const initialY = editorRect.top + 20

    // Set position directly on the element for initial positioning
    panel.style.left = `${initialX}px`
    panel.style.top = `${initialY}px`
  }, [editorContainerRef, position.x, position.y])

  // Close panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <div
      ref={panelRef}
      className="bg-white rounded-lg shadow-2xl z-[1000] pointer-events-auto"
      style={{
        position: 'fixed',
        left: position.x !== 0 ? `${position.x}px` : undefined,
        top: position.y !== 0 ? `${position.y}px` : undefined,
        width: '350px',
        maxHeight: '500px',
        border: '1px solid #D1D5DB', // gray-300
        cursor: isDragging ? 'grabbing' : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: 'var(--dq-brand)' }}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-2 text-white">
          <FontAwesomeIcon icon={faSearch} />
          <span className="font-semibold">Usages: {listName}</span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
          aria-label="Close usages panel"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {usages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No usages found</p>
            <p className="text-sm mt-2">This list is not referenced anywhere.</p>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 mb-3">
              Found {usages.length} usage{usages.length !== 1 ? 's' : ''}
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {usages.map((usage, index) => {
                const usageType = getUsageType(usage.content, listName, index)
                const badgeColor = getBadgeColor(usageType)

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded p-3 hover:bg-cyan-50 hover:border-cyan-300 transition-all cursor-pointer"
                    onClick={() => onLineClick(usage.lineNumber)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                          Line {usage.lineNumber}
                        </span>
                        {usageType && (
                          <span className={`text-xs text-white px-2 py-1 rounded ${badgeColor}`}>
                            {usageType}
                          </span>
                        )}
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        className="text-xs flex-shrink-0"
                        style={{ color: 'var(--dq-brand)' }}
                      />
                    </div>
                    <div className="text-sm font-mono text-gray-700 break-all">
                      {usage.content.trim()}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <div className="text-xs text-gray-500 flex items-center justify-between">
          <span>Click a usage to navigate</span>
          <span className="font-mono">ESC to close</span>
        </div>
      </div>
    </div>
  )
}
